import { Route } from "wouter";
import { Suspense } from "react";
// lazyWithReload = React.lazy + one page reload if the chunk vanished after a deploy (DEV-321)
import { lazyWithReload as lazy } from "@/lib/appVersion";

const LazySuperadminDashboard = lazy(() => import("@/pages/superadmin/Dashboard"));
const SuperadminBugReports = lazy(() => import("@/pages/superadmin/BugReports"));
const SuperadminUserManagement = lazy(() => import("@/pages/superadmin/UserManagement"));
const SuperadminAgencyManagement = lazy(() => import("@/pages/superadmin/AgencyManagement"));
const LazyAdminCarriers = lazy(() => import("@/pages/admin/AdminCarriers"));
const LazyAdminStates = lazy(() => import("@/pages/admin/AdminStates"));
const LazyRateAreas = lazy(() => import("@/pages/superadmin/RateAreas"));
const LazyPlanBuilder = lazy(() => import("@/pages/admin/PlanBuilder"));
const LazyAdminFormLibrary = lazy(() => import("@/pages/admin/FormLibrary"));
const LazyCarriers = lazy(() => import("@/pages/broker/Carriers"));
const LazyKnowledgebase = lazy(() => import("@/pages/superadmin/Knowledgebase"));
const LazyKnowledgebaseEditor = lazy(() => import("@/pages/superadmin/KnowledgebaseArticleEditor"));
const LazyAiTraining = lazy(() => import("@/pages/superadmin/AiTraining"));
const LazySettings = lazy(() => import("@/pages/superadmin/Settings"));
const LazyTestCoverage = lazy(() => import("@/pages/superadmin/TestCoverage"));
const LazyClients = lazy(() => import("@/pages/superadmin/Clients"));
const LazyClientRecord = lazy(() => import("@/pages/superadmin/ClientRecord"));
const LazyLeads = lazy(() => import("@/pages/superadmin/Leads"));
const LazyLeadRecord = lazy(() => import("@/pages/superadmin/LeadRecord"));
const LazyOpportunities = lazy(() => import("@/pages/superadmin/Opportunities"));
const LazyOpportunityRecord = lazy(() => import("@/pages/superadmin/OpportunityRecord"));
const LazyProposals = lazy(() => import("@/pages/superadmin/Proposals"));
const LazyProposalEditor = lazy(() => import("@/pages/superadmin/ProposalEditor"));
const LazyOnboardingTracker = lazy(() => import("@/pages/superadmin/OnboardingTracker"));
const LazyIntegrations = lazy(() => import("@/pages/superadmin/Integrations"));
const LazyFeatureAnalytics = lazy(() => import("@/pages/superadmin/FeatureAnalytics"));
const LazyClientHealth = lazy(() => import("@/pages/superadmin/ClientHealth"));
const LazyAiEvals = lazy(() => import("@/pages/superadmin/AiEvals"));
const LazyMarketing = lazy(() => import("@/pages/superadmin/Marketing"));
const LazyWorkflows = lazy(() => import("@/pages/superadmin/Workflows"));
const LazyWorkflowEditor = lazy(() => import("@/pages/superadmin/WorkflowEditor"));

function LazyWrap({ Component }: { Component: React.LazyExoticComponent<any> }) {
  return (
    <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading...</div>}>
      <Component />
    </Suspense>
  );
}

// Non-lazy dashboard wrapper for the "/" route
export function SuperadminDashboard() {
  return <LazyWrap Component={LazySuperadminDashboard} />;
}

export function SuperadminRoutes() {
  return (
    <>
      <Route path="/superadmin">
        <LazyWrap Component={LazySuperadminDashboard} />
      </Route>
      <Route path="/superadmin/bug-reports">
        <LazyWrap Component={SuperadminBugReports} />
      </Route>
      <Route path="/superadmin/users">
        <LazyWrap Component={SuperadminUserManagement} />
      </Route>
      <Route path="/superadmin/agencies">
        <LazyWrap Component={SuperadminAgencyManagement} />
      </Route>
      <Route path="/superadmin/carriers">
        <LazyWrap Component={LazyAdminCarriers} />
      </Route>
      <Route path="/superadmin/states">
        <LazyWrap Component={LazyAdminStates} />
      </Route>
      <Route path="/superadmin/rate-areas">
        <LazyWrap Component={LazyRateAreas} />
      </Route>
      <Route path="/superadmin/plan-library">
        <LazyWrap Component={LazyPlanBuilder} />
      </Route>
      <Route path="/superadmin/form-library">
        <LazyWrap Component={LazyAdminFormLibrary} />
      </Route>
      <Route path="/superadmin/knowledge-base">
        <LazyWrap Component={LazyKnowledgebase} />
      </Route>
      <Route path="/superadmin/knowledge-base/articles/new">
        <LazyWrap Component={LazyKnowledgebaseEditor} />
      </Route>
      <Route path="/superadmin/knowledge-base/articles/:id">
        <LazyWrap Component={LazyKnowledgebaseEditor} />
      </Route>
      <Route path="/superadmin/ai-training">
        <LazyWrap Component={LazyAiTraining} />
      </Route>
      <Route path="/superadmin/settings">
        <LazyWrap Component={LazySettings} />
      </Route>
      <Route path="/superadmin/test-coverage">
        <LazyWrap Component={LazyTestCoverage} />
      </Route>
      <Route path="/superadmin/clients">
        <LazyWrap Component={LazyClients} />
      </Route>
      <Route path="/superadmin/clients/:id">
        <LazyWrap Component={LazyClientRecord} />
      </Route>
      <Route path="/superadmin/leads">
        <LazyWrap Component={LazyLeads} />
      </Route>
      <Route path="/superadmin/leads/:id">
        <LazyWrap Component={LazyLeadRecord} />
      </Route>
      <Route path="/superadmin/opportunities">
        <LazyWrap Component={LazyOpportunities} />
      </Route>
      <Route path="/superadmin/opportunities/:id">
        <LazyWrap Component={LazyOpportunityRecord} />
      </Route>
      <Route path="/superadmin/proposals">
        <LazyWrap Component={LazyProposals} />
      </Route>
      <Route path="/superadmin/proposals/:id">
        <LazyWrap Component={LazyProposalEditor} />
      </Route>
      <Route path="/superadmin/onboarding">
        <LazyWrap Component={LazyOnboardingTracker} />
      </Route>
      <Route path="/superadmin/integrations">
        <LazyWrap Component={LazyIntegrations} />
      </Route>
      <Route path="/superadmin/reports/feature-analytics">
        <LazyWrap Component={LazyFeatureAnalytics} />
      </Route>
      <Route path="/superadmin/reports/client-health">
        <LazyWrap Component={LazyClientHealth} />
      </Route>
      <Route path="/superadmin/reports/ai-evals">
        <LazyWrap Component={LazyAiEvals} />
      </Route>
      <Route path="/superadmin/marketing">
        <LazyWrap Component={LazyMarketing} />
      </Route>
      <Route path="/superadmin/workflows">
        <LazyWrap Component={LazyWorkflows} />
      </Route>
      <Route path="/superadmin/workflows/:id">
        <LazyWrap Component={LazyWorkflowEditor} />
      </Route>
      <Route path="/carriers">
        <LazyWrap Component={LazyCarriers} />
      </Route>
      <Route path="/internal/form-library">
        <LazyWrap Component={LazyAdminFormLibrary} />
      </Route>
    </>
  );
}
